import React from 'react';
import { Card } from '../../../../components/ui/Card';
import { Button } from '../../../../components/ui/Button/Button';
import { Plus, Eye, Edit, Mail } from 'lucide-react';

interface CampaignTemplatesProps {
  data: any;
  onChange: (field: string, value: any) => void;
  errors: Record<string, string>;
}

const mockTemplates = [
  {
    id: '1',
    name: 'Cold Outreach Template',
    subject: 'Quick question about {company}',
    type: 'cold',
    openRate: 68.5,
    replyRate: 12.3
  },
  {
    id: '2',
    name: 'Follow-up Template',
    subject: 'Following up on my previous email',
    type: 'followup',
    openRate: 45.2,
    replyRate: 8.7
  },
  {
    id: '3',
    name: 'Product Demo Template',
    subject: 'See how {company} can save 40% on costs',
    type: 'demo',
    openRate: 72.1,
    replyRate: 15.6
  }
];

export const CampaignTemplates: React.FC<CampaignTemplatesProps> = ({ data, onChange, errors }) => {
  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 mb-2">
          Email Templates
        </h2>
        <p className="text-neutral-600 dark:text-neutral-400">
          Choose and customize your email templates
        </p>
      </div>

      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
          Available Templates
        </h3>
        <Button variant="outline">
          <Plus className="w-4 h-4 mr-2" />
          Create Template
        </Button>
      </div>

      <div className="grid gap-4">
        {mockTemplates.map((template) => (
          <Card key={template.id} className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                  <Mail className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <h4 className="font-semibold text-neutral-900 dark:text-neutral-100">
                    {template.name}
                  </h4>
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">
                    {template.subject}
                  </p>
                  <div className="flex items-center space-x-4 mt-2">
                    <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-2 py-1 rounded-full">
                      {template.openRate}% open rate
                    </span>
                    <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 px-2 py-1 rounded-full">
                      {template.replyRate}% reply rate
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Button variant="ghost" size="sm">
                  <Eye className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm">
                  <Edit className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="sm">
                  Select
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      <Card className="p-6">
        <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-4">
          Template Editor
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              Subject Line
            </label>
            <input
              type="text"
              placeholder="Enter subject line..."
              className="w-full px-3 py-2 border border-neutral-200 dark:border-neutral-700 rounded-md bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              Email Body
            </label>
            <textarea
              placeholder="Write your email content here..."
              rows={10}
              className="w-full px-3 py-2 border border-neutral-200 dark:border-neutral-700 rounded-md bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100"
            />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-neutral-600 dark:text-neutral-400">
                Use variables like {'{firstName}'}, {'{company}'}, {'{title}'} for personalization
              </p>
            </div>
            <Button variant="outline">
              Save Template
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}; 